import { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { useLocale } from '../../utils/LocaleContext';
import { S } from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// Inject spinner animation once
if (typeof document !== 'undefined' && !document.getElementById('bord-spin-css')) {
  const s = document.createElement('style');
  s.id = 'bord-spin-css';
  s.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(s);
}

const useBordereauxFunctions = () => {
  useLocale();
  const history = useHistory();
  const [bordereaux, setBordereaux] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  const [filterCarrier, setFilterCarrier] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showGenModal, setShowGenModal] = useState(false);
  const [sending, setSending] = useState(null);

  const fetchBordereaux = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (filterCarrier) params.carrierId = filterCarrier;
      if (filterType) params.type = filterType;
      if (filterStatus) params.status = filterStatus;
      const resp = await axios.get(`${BASE_URL}/bordereaux`, { params, withCredentials: true });
      if (resp.data?.data) {
        setBordereaux(resp.data.data);
        setTotal(resp.data.total || 0);
      }
    } catch {
      /* fallback empty */
    } finally {
      setLoading(false);
    }
  }, [page, filterCarrier, filterType, filterStatus]);

  const fetchCarriers = useCallback(async () => {
    try {
      const resp = await axios.get(`${BASE_URL}/risk-carriers`, { params: { limit: 200 }, withCredentials: true });
      if (resp.data?.data) setCarriers(resp.data.data.map(c => ({ id: c._id, name: c.name })));
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchBordereaux();
  }, [fetchBordereaux]);
  useEffect(() => {
    fetchCarriers();
  }, [fetchCarriers]);

  // R-05 / R-06: window.open on a cookie-auth endpoint sometimes lost
  // credentials in Chrome (new-tab context) and left the tester on a
  // blank page with no error. Switch to axios blob download with
  // withCredentials so the file is fetched with cookies, wrapped in a
  // blob URL, and a hidden <a download> click triggers the save
  // dialog. Any 404/500 now surfaces via an alert instead of a silent
  // tab that never renders anything.
  const handleDownload = async (id) => {
    try {
      const resp = await axios.get(`${BASE_URL}/bordereaux/${id}/download`, {
        responseType: 'blob',
        withCredentials: true,
      });
      const disp = resp.headers['content-disposition'] || '';
      const m = disp.match(/filename="?([^"]+)"?/i);
      const filename = m ? m[1] : `bordereaux-${id}`;
      const blobUrl = window.URL.createObjectURL(new Blob([resp.data], { type: resp.headers['content-type'] || 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 0);
    } catch (err) {
      let msg = 'Download failed';
      // When responseType is 'blob', the error body is also a Blob;
      // read it as text to surface the backend AppError message.
      if (err.response?.data instanceof Blob) {
        try {
          const txt = await err.response.data.text();
          const parsed = JSON.parse(txt);
          msg = parsed.message || msg;
        } catch { /* keep default */ }
      } else if (err.response?.data?.message) {
        msg = err.response.data.message;
      }
      alert(msg);
    }
  };

  const handleSend = async id => {
    setSending(id);
    try {
      await axios.post(`${BASE_URL}/bordereaux/${id}/send`, {}, { withCredentials: true });
      setBordereaux(prev => prev.map(b => (b._id === id ? { ...b, status: 'sent' } : b)));
    } catch {
      /* silent */
    } finally {
      setSending(null);
    }
  };

  // Deletion happens inside the ConfirmDialog's onConfirm — this hook
  // just exposes a low-level runner that the screen wires the modal to.
  const runDelete = async id => {
    await axios.delete(`${BASE_URL}/bordereaux/${id}`, { withCredentials: true });
    setBordereaux(prev => prev.filter(b => b._id !== id));
    setTotal(prev => Math.max(0, prev - 1));
  };

  const selStyle = {
    padding: '8px 12px',
    background: '#fff',
    border: `1px solid ${S.borderLight}`,
    borderRadius: 8,
    fontSize: 13,
    color: S.textPrimary,
    outline: 'none',
    cursor: 'pointer',
  };

  // Stats
  const totalReady = bordereaux.filter(b => b.status === 'ready').length;
  const totalSent = bordereaux.filter(b => b.status === 'sent').length;
  const totalErr = bordereaux.filter(b => b.status === 'error').length;

  return {
    history,
    bordereaux,
    setBordereaux,
    carriers,
    setCarriers,
    loading,
    setLoading,
    total,
    setTotal,
    page,
    setPage,
    PAGE_SIZE,
    filterCarrier,
    setFilterCarrier,
    filterType,
    setFilterType,
    filterStatus,
    setFilterStatus,
    showGenModal,
    setShowGenModal,
    sending,
    setSending,
    fetchBordereaux,
    fetchCarriers,
    handleDownload,
    handleSend,
    runDelete,
    selStyle,
    totalReady,
    totalSent,
    totalErr,
  };
};

export default useBordereauxFunctions;
